/**
 * /pipeline-facility/rates — the PF rate card (GET /pipeline-facility/rates,
 * fieldiq-api#1227, PF-4).
 *
 * Full width. Filters in the URL: the card (`card=default`, a customer's id,
 * or every card) and the kind. A rate reads in the company's currency format —
 * `CA$95.00/hr` — and an absent figure is `—`. New and edit are full pages
 * (RateDetailScreen), never a dialog.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfCustomers, usePfMeta } from '../../../hooks/usePipelineFacility';
import { usePfRates } from '../../../hooks/usePfTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canWritePfTickets } from '../../../lib/pf-tickets';
import { catalogLabel, pfDay } from '../../../lib/pipeline-facility';
import type { PfRate } from '../../../types/pf-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';
import { pfRateText } from './TicketBits';

/** What a rate prices beyond its kind: the crew role, the unit or the equipment type. */
export function pfRateAppliesTo(r: PfRate, crewRoles?: { value: string; label: string }[]) {
  if (r.crewRole) return catalogLabel(crewRoles, r.crewRole);
  if (r.equipmentId) return r.equipmentUnitNumber ? `${r.equipmentUnitNumber} · ${r.equipmentName ?? '—'}` : r.equipmentName || '—';
  if (r.equipmentTypeId) return r.equipmentTypeName || '—';
  return '—';
}

export default function PfRatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canWrite = canWritePfTickets(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const card = searchParams.get('card') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const catalog = usePfMeta().data?.data ?? null;
  const customers = usePfCustomers().data ?? [];
  const listQuery = usePfRates({ customer_id: card || undefined, kind: kind || undefined });
  const allQuery = usePfRates();
  const rates = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: PfRate) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const today = allQuery.data?.meta.today ?? null;
  const live = (r: PfRate) => !!today && r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today);
  const filtered = !!(card || kind);

  const columns: PfColumn<PfRate>[] = [
    { key: 'code', header: 'Code', render: (r) => (<><p className="font-mono font-extrabold text-white">{r.code || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'}</p></>) },
    { key: 'card', header: 'Card', render: (r) => (r.customerId ? r.customerName || '—' : 'Default card') },
    { key: 'kind', header: 'Kind', render: (r) => r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind) },
    { key: 'applies', header: 'Applies to', render: (r) => pfRateAppliesTo(r, catalog?.crewRoles) },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => <span className="font-mono">{pfRateText(r.rate, r.currency, r.unit, r.distanceUnit)}</span> },
    { key: 'period', header: 'Effective', render: (r) => `${pfDay(r.effectiveFrom)} – ${r.effectiveTo ? pfDay(r.effectiveTo) : 'no end'}` },
  ];

  return (
    <div data-testid="pf-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Rate Card"
        description="What the company bills, by crew role, equipment, rent-in and charge — on the default card or a customer's own."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'In effect today', tone: 'accent', loading: allQuery.isLoading, value: countOf(live) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Customer rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => !!r.customerId) },
        ]}
        actions={
          canWrite ? (
            <button
              type="button"
              className={DH_BAND_SECONDARY}
              onClick={() => navigate(`/pipeline-facility/rates/new${card && card !== 'default' ? `?customerId=${card}` : ''}`)}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfChipRow label="Filter by kind">
            <PfChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {(catalog?.priceKinds ?? []).map((k) => (
              <PfChip key={k.value} label={k.label} active={kind === k.value} onClick={() => setParam('kind', k.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.kind === k.value)} />
            ))}
          </PfChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={PF_INPUT} aria-label="Filter by card" value={card} onChange={(e) => setParam('card', e.target.value)}>
              <option value="">Every card</option>
              <option value="default">Default card</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <PfPanel eyebrow="Rate card" title="Rates" testId="pf-rate-register" footer={!listQuery.isLoading && rates.length ? `${rates.length} rate${rates.length === 1 ? '' : 's'}` : undefined}>
            <PfRegister
              query={listQuery}
              rows={rates}
              columns={columns}
              rail={(r) => (live(r) ? 'border-l-fi-green' : 'border-l-fi-steel')}
              onOpen={(r) => openRecord(`/pipeline-facility/rates/${r.id}`)}
              subject="rates"
              testId="pf-rate"
              minWidth="min-w-[920px]"
              empty={filtered ? <PfEmpty>No rate matches these filters.</PfEmpty> : <PfEmpty>No rates yet. Add the first one to price field tickets.</PfEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.code || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'} · {r.customerId ? r.customerName || '—' : 'Default card'}</p>
                    </div>
                    <span className="font-mono text-sm text-white">{pfRateText(r.rate, r.currency, r.unit, r.distanceUnit)}</span>
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} · {pfRateAppliesTo(r, catalog?.crewRoles)} · from {pfDay(r.effectiveFrom)}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
